import React, { useState, useContext, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL } from '../../utils/constants';
import { FaCamera, FaUpload, FaTrash, FaImage } from 'react-icons/fa';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './UpdateProfilePictureScreen.css';

const UpdateProfilePictureScreen = () => {
  const { user, setUser } = useUser();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Please select an image smaller than 5MB.');
        return;
      }

      setSelectedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedImage) {
      alert('Please select an image first.');
      return;
    }

    try {
      setUploading(true);
      const accessToken = localStorage.getItem('ACCESS_TOKEN');

      const formData = new FormData();
      formData.append('profilePicture', selectedImage);

      const response = await fetch(`${API_BASE_URL}api/doctor/profile/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update user context
        setUser({
          ...user,
          profilePicture: data?.user?.profilePicture,
        });

        alert('Profile picture updated successfully!');
        setSelectedImage(null);
        setPreviewUrl(null);
      } else {
        throw new Error('Failed to upload profile picture');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert('Failed to upload profile picture. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getCurrentImageUrl = () => {
    if (previewUrl) return previewUrl;
    if (user?.profilePicture?.secure_url) return user.profilePicture.secure_url;
    return null;
  };

  return (
    <DashboardLayout title="Update Profile Picture" showBackButton={true}>
      <div className="update-profile-container">
        {/* Header */}
        <div className="update-header">
          <h1 className="update-title">Update Profile Picture</h1>
          <p className="update-subtitle">
            Choose a professional picture to represent yourself on your profile.
          </p>
        </div>

        {/* Image Preview Section */}
        <div className="image-preview-section">
          <div className="image-container">
            {getCurrentImageUrl() ? (
              <div className="image-wrapper">
                <img
                  src={getCurrentImageUrl()}
                  alt="Profile Preview"
                  className="profile-image"
                />
                <div className="image-overlay">
                  <button 
                    className="overlay-button change-button"
                    onClick={triggerFileInput}
                  >
                    <FaCamera />
                    <span>Change</span>
                  </button>
                  {previewUrl && (
                    <button 
                      className="overlay-button remove-button"
                      onClick={handleRemoveImage}
                    >
                      <FaTrash />
                      <span>Remove</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="placeholder-container">
                <div className="placeholder-icon">
                  <FaImage />
                </div>
                <p className="placeholder-text">No image selected</p>
                <button 
                  className="select-image-button"
                  onClick={triggerFileInput}
                >
                  <FaCamera />
                  <span>Select Image</span>
                </button>
              </div>
            )}
          </div>

          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="file-input"
          />
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {!getCurrentImageUrl() && (
            <button 
              className="action-button select-button"
              onClick={triggerFileInput}
            >
              <FaCamera />
              <span>Select Image</span>
            </button>
          )}

          {selectedImage && (
            <button 
              className="action-button upload-button"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <FaUpload />
                  <span>Upload Picture</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Guidelines */}
        <div className="guidelines-section">
          <h3 className="guidelines-title">Image Guidelines</h3>
          <ul className="guidelines-list">
            <li>Use a clear, professional headshot</li>
            <li>Ensure good lighting and high resolution</li>
            <li>Face should be clearly visible</li>
            <li>Maximum file size: 5MB</li>
            <li>Supported formats: JPG, PNG, GIF</li>
            <li>Square images work best (1:1 ratio)</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UpdateProfilePictureScreen;